import React from 'react';
import Sidebar from './Sidebar';
import { dashboardMetrics } from '../data/dummyData';

interface DashboardProps {
  onViewQuotations: () => void;
  onNavigate: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onViewQuotations, onNavigate }) => {
  const handleNavigate = (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => {
    if (view === 'quotations') {
      onViewQuotations();
    } else {
      onNavigate(view);
    }
  };

  const metrics = [
    {
      title: 'Total Quotations',
      value: dashboardMetrics.totalQuotations,
      change: '+12%',
      changeType: 'positive',
      icon: '📊'
    },
    {
      title: 'New Quotations',
      value: dashboardMetrics.newQuotations,
      change: '+8%',
      changeType: 'positive',
      icon: '🆕'
    },
    {
      title: 'Quoted',
      value: dashboardMetrics.quotedQuotations,
      change: '+15%',
      changeType: 'positive',
      icon: '📝'
    },
    {
      title: 'Won',
      value: dashboardMetrics.wonQuotations,
      change: '+23%',
      changeType: 'positive',
      icon: '✅'
    },
    {
      title: 'Conversion Rate',
      value: `${dashboardMetrics.conversionRate}%`,
      change: '+5.2%',
      changeType: 'positive',
      icon: '📈'
    },
    {
      title: 'Avg Response Time',
      value: dashboardMetrics.avgResponseTime,
      change: '-0.8h',
      changeType: 'positive',
      icon: '⏱️'
    },
    {
      title: 'Total Value',
      value: `$${dashboardMetrics.totalValue.toLocaleString()}`,
      change: '+18%',
      changeType: 'positive',
      icon: '💰'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar currentView="dashboard" onNavigate={handleNavigate} />

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">AI Quotation Management</h1>
              <p className="text-gray-600 mt-1">Intelligent quotation processing and tracking</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Last updated</p>
                <p className="text-sm font-medium text-gray-900">2 minutes ago</p>
              </div>
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">AI</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div key={index} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                </div>
                <div className="text-2xl">{metric.icon}</div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${
                  metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {metric.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">vs last month</span>
              </div>
            </div>
          ))}
        </div>

        {/* Charts and Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Channel Distribution */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quotations by Channel</h3>
            <div className="space-y-3">
              {dashboardMetrics.topChannels.map((channel, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      index === 0 ? 'bg-blue-500' :
                      index === 1 ? 'bg-green-500' :
                      index === 2 ? 'bg-yellow-500' : 'bg-purple-500'
                    }`}></div>
                    <span className="text-sm font-medium text-gray-700">{channel.channel}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{channel.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {dashboardMetrics.recentActivity.map((activity, index) => {
                const getActivityColor = (action: string) => {
                  if (action.includes('New quotation')) return 'bg-blue-100 text-blue-800';
                  if (action.includes('Quotation sent')) return 'bg-green-100 text-green-800';
                  if (action.includes('Won')) return 'bg-emerald-100 text-emerald-800';
                  if (action.includes('Lost')) return 'bg-red-100 text-red-800';
                  if (action.includes('Follow-up')) return 'bg-yellow-100 text-yellow-800';
                  return 'bg-gray-100 text-gray-800';
                };

                return (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActivityColor(activity.action)}`}>
                        {activity.action.includes('New quotation') ? '🆕' :
                         activity.action.includes('Quotation sent') ? '📤' :
                         activity.action.includes('Won') ? '✅' :
                         activity.action.includes('Lost') ? '❌' :
                         activity.action.includes('Follow-up') ? '📞' : '📋'}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                        <p className="text-xs text-gray-500">ID: {activity.id}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
